import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, merge, Observable } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import * as appointmentsActions from '@appointments/store/actions/appointments.actions';
import * as commentsActions from '@comments/store/actions/comments.actions';
import * as listingActivityActions from '@listings/store/actions/listing-activity.actions';
import * as profileActions from '@profile/store/actions/profile.actions';
import * as profileBaseActions from 'app/modules/user-modules/profile-modules/profile-base/store/actions/profile-base.actions';
import { FUB_APPOINTMENT_STATUS_EVENT_SOURCE_MAP, FUB_LISTING_ACTIVITY_EVENT_SOURCE_MAP } from '@core-constants/fub-analytics.constants';
import { FubAnalyticsApiService } from '@core-services/fub-analytics/fub-analytics-api.service';
import { OutboundInquiryEventSource } from 'app/graphql/graphql';

@Injectable()
export class FubAnalyticsEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly fubAnalyticsApiService: FubAnalyticsApiService,
    ) { }

    public readonly listingReactionSet$ = createEffect(
        () => this.actions$.pipe(
            ofType(listingActivityActions.setListingsActivitySuccess),
            mergeMap(({ request }) => {
                const eventSource = FUB_LISTING_ACTIVITY_EVENT_SOURCE_MAP.get(request.activity.id);

                if (eventSource == null || request.isReactionRemoved) {
                    return EMPTY;
                }

                return merge(...request.listingCandidates.map(({ hashCode }) => this.record(eventSource, hashCode)));
            })
        ),
        { dispatch: false }
    );

    public readonly appointmentCreated$ = createEffect(
        () => this.actions$.pipe(
            ofType(appointmentsActions.createListingAppointmentSuccess),
            mergeMap(({ appointment }) =>
                this.record(OutboundInquiryEventSource.AppointmentCreated, appointment.listingIdHashCode, appointment.comment, appointment.id))
        ),
        { dispatch: false }
    );

    public readonly appointmentUpdated$ = createEffect(
        () => this.actions$.pipe(
            ofType(appointmentsActions.updateListingAppointmentSuccess),
            mergeMap(({ request: { listingAppointment } }) =>
                this.record(OutboundInquiryEventSource.AppointmentEdited, listingAppointment.listingIdHashCode, listingAppointment.comment, listingAppointment.id))
        ),
        { dispatch: false }
    );

    public readonly appointmentStatusChanged$ = createEffect(
        () => this.actions$.pipe(
            ofType(appointmentsActions.changeAppointmentStatusSuccess),
            mergeMap(({ request }) => {
                const eventSource = FUB_APPOINTMENT_STATUS_EVENT_SOURCE_MAP.get(request.customerStatus);

                return eventSource == null ? EMPTY : this.record(eventSource, request.listingHashCode, null, request.appointmentId);
            })
        ),
        { dispatch: false }
    );

    public readonly commentCreated$ = createEffect(
        () => this.actions$.pipe(
            ofType(commentsActions.createListingCommentSuccess),
            mergeMap(({ comment }) =>
                this.record(OutboundInquiryEventSource.CommentCreated, comment.listingIdHashCode, comment.comment, comment.id))
        ),
        { dispatch: false }
    );

    public readonly commentUpdated$ = createEffect(
        () => this.actions$.pipe(
            ofType(commentsActions.updateListingCommentSuccess),
            mergeMap(({ listingIdHashCode, newComment, oldListingComment }) =>
                this.record(OutboundInquiryEventSource.CommentUpdated, listingIdHashCode, newComment, oldListingComment.id))
        ),
        { dispatch: false }
    );

    public readonly childCustomerInvited$ = createEffect(
        () => this.actions$.pipe(
            ofType(profileBaseActions.inviteUserSuccessful),
            mergeMap(({ childEmail, message }) => this.recordChildInvite(childEmail, message))
        ),
        { dispatch: false }
    );

    public readonly childCustomerInviteResent$ = createEffect(
        () => this.actions$.pipe(
            ofType(profileBaseActions.reSendInviteSuccessful),
            mergeMap(({ oldEmail, newEmail, message }) => this.recordChildInvite(newEmail ?? oldEmail, message))
        ),
        { dispatch: false }
    );

    public readonly customerProfileUpdated$ = createEffect(
        () => this.actions$.pipe(
            ofType(profileActions.updateCustomerInfoSuccessful, profileActions.updateAccountDataSuccessful),
            switchMap(() => this.syncCustomerToFub())
        ),
        { dispatch: false }
    );

    // Fire-and-forget: reporting to Follow Up Boss must never surface errors to the user.
    // Plain action values only — the BE derives the Ccp*-prefixed publisher variant from the token.
    // activityId is the appointment/comment id; the BE re-reads the entity to build the rich note.
    private record(eventSource: OutboundInquiryEventSource, listingId?: number, message?: string | null, activityId?: number): Observable<boolean> {
        return this.fubAnalyticsApiService.record({
            eventSource,
            listingId: listingId ?? null,
            isBuilding: false,
            message: message ? message : null,
            activityId: activityId ?? null
        }).pipe(catchError(() => EMPTY));
    }

    private recordChildInvite(recipientEmail: string, message?: string | null): Observable<boolean> {
        return this.fubAnalyticsApiService.recordChildInvite(recipientEmail, message ? message : null)
            .pipe(catchError(() => EMPTY));
    }

    private syncCustomerToFub(): Observable<boolean> {
        return this.fubAnalyticsApiService.syncCustomerToFub().pipe(catchError(() => EMPTY));
    }
}
